"use client"

import { useEffect } from "react"
import type { RefObject } from "react"
import { useLatestRef } from "@/hooks/useLatestRef"
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion"
import {
    countRollDirection,
    ISLAND_FADE,
    islandSpring,
} from "./islandMotion.utils"
import { isWilsonBarMorphing } from "./viewTransition.utils"

const WATCHED: MutationObserverInit = {
    subtree: true, // boolean-naming-exempt: external contract — MutationObserverInit
    childList: true, // boolean-naming-exempt: external contract — MutationObserverInit
    characterData: true, // boolean-naming-exempt: external contract — MutationObserverInit
    characterDataOldValue: true, // boolean-naming-exempt: external contract — MutationObserverInit
    attributes: true, // boolean-naming-exempt: external contract — MutationObserverInit
    attributeFilter: ["class"],
    attributeOldValue: true, // boolean-naming-exempt: external contract — MutationObserverInit
}

const ATTACHED_WATCHED: MutationObserverInit = {
    attributes: true, // boolean-naming-exempt: external contract — MutationObserverInit
    attributeFilter: ["data-attached"],
}

const FADE_IN: Keyframe[] = [
    { opacity: 0, scale: "0.96" },
    { opacity: 1, scale: "1" },
]
const FADE_IN_DIRECTION = 0

function rollIn(direction: number): Keyframe[] {
    return [
        { opacity: 0, translate: `0 ${direction * 60}%` },
        { opacity: 1, translate: "0 0" },
    ]
}

function translateXOf(el: HTMLElement): number {
    const { transform } = getComputedStyle(el)
    return transform === "none" ? 0 : new DOMMatrixReadOnly(transform).m41
}

const STRETCH_SELECTOR = ":scope > [data-island-stretch]"

function fitContentToIsland({
    content,
    stretch,
}: {
    content: HTMLElement
    stretch: HTMLElement | null
}) {
    content.style.flex = "1 1 0%"
    content.style.minWidth = "0"
    if (!stretch) return
    stretch.style.flex = "1 1 auto"
    stretch.style.minWidth = "0"
}

function releaseContent({
    content,
    stretch,
}: {
    content: HTMLElement
    stretch: HTMLElement | null
}) {
    content.style.flex = ""
    content.style.minWidth = ""
    if (!stretch) return
    stretch.style.flex = ""
    stretch.style.minWidth = ""
}

function isHidden(el: HTMLElement): boolean {
    return el.offsetWidth === 0
}

function wasUnhidden(record: MutationRecord): boolean {
    return (
        record.target instanceof HTMLElement &&
        !record.target.classList.contains("hidden") &&
        (record.oldValue ?? "").split(/\s+/).includes("hidden")
    )
}

function arrivalDirections(
    records: MutationRecord[],
    content: HTMLElement
): Map<HTMLElement, number> {
    const changed = new Map<HTMLElement, number>()
    for (const record of records) {
        if (record.type === "characterData") {
            const el = record.target.parentElement
            if (el) {
                changed.set(
                    el,
                    countRollDirection({
                        before: record.oldValue,
                        after: record.target.textContent,
                    })
                )
            }
        } else if (record.type === "childList") {
            record.addedNodes.forEach(node => {
                const el =
                    node instanceof HTMLElement ? node : node.parentElement
                if (el && !changed.has(el)) {
                    changed.set(el, FADE_IN_DIRECTION)
                }
            })
        } else if (
            record.target.parentElement === content &&
            record.target instanceof HTMLElement &&
            wasUnhidden(record)
        ) {
            changed.set(record.target, FADE_IN_DIRECTION)
        }
    }
    const outermost = new Map<HTMLElement, number>()
    changed.forEach((direction, el) => {
        if (el === content || !content.contains(el) || isHidden(el)) return
        let ancestor = el.parentElement
        while (ancestor && ancestor !== content) {
            if (changed.has(ancestor)) return
            ancestor = ancestor.parentElement
        }
        outermost.set(el, direction)
    })
    return outermost
}

export function useIslandMotion({
    islandRef,
    contentRef,
}: {
    islandRef: RefObject<HTMLElement | null>
    contentRef: RefObject<HTMLElement | null>
}) {
    const isReducedMotionPreferred = usePrefersReducedMotion()
    const isReducedMotionPreferredRef = useLatestRef(isReducedMotionPreferred)

    useEffect(() => {
        const island = islandRef.current
        const content = contentRef.current
        if (!island || !content || typeof island.animate !== "function") {
            return
        }
        const offsets = new WeakMap<HTMLElement, number>()
        const glides = new Map<HTMLElement, Animation>()
        let islandWidth = island.offsetWidth
        let morph: Animation | null = null
        let pending: MutationRecord[] = []
        let frame = 0
        let stretched: HTMLElement | null = null
        let isEmptyBefore = false

        const items = () =>
            Array.from(content.children).filter(
                (child): child is HTMLElement => child instanceof HTMLElement
            )
        const centerOffsetOf = (el: HTMLElement) =>
            el.offsetLeft + el.offsetWidth / 2 - content.offsetWidth / 2
        const rememberOffsets = () => {
            for (const item of items()) {
                if (isHidden(item)) offsets.delete(item)
                else offsets.set(item, centerOffsetOf(item))
            }
        }
        const settleMorph = () => {
            morph = null
            island.style.overflow = ""
            if (!stretched) return
            releaseContent({ content, stretch: stretched })
            stretched = null
            rememberOffsets()
        }
        const stopMorph = () => {
            morph?.cancel()
            settleMorph()
        }
        const stopGlides = () => {
            glides.forEach(glide => glide.cancel())
            glides.clear()
        }

        const animateChange = () => {
            frame = 0
            const records = pending
            pending = []
            if (isReducedMotionPreferredRef.current || isWilsonBarMorphing()) {
                stopMorph()
                stopGlides()
                islandWidth = island.offsetWidth
                isEmptyBefore = items().every(isHidden)
                rememberOffsets()
                return
            }

            const spring = islandSpring()
            const fromWidth = morph ? island.offsetWidth : islandWidth
            const fromOffsets = new Map<HTMLElement, number>()
            for (const item of items()) {
                const offset = offsets.get(item)
                if (offset !== undefined) {
                    fromOffsets.set(item, offset + translateXOf(item))
                }
            }
            stopMorph()
            stopGlides()

            const toWidth = island.offsetWidth
            const isGrowingFromEmpty = isEmptyBefore && !morph
            isEmptyBefore = items().every(isHidden)
            const isResizing =
                !isGrowingFromEmpty && Math.abs(toWidth - fromWidth) > 0.5
            if (isResizing) {
                stretched = content.querySelector<HTMLElement>(STRETCH_SELECTOR)
                fitContentToIsland({ content, stretch: stretched })
                island.style.overflow = "hidden"
                const current = island.animate(
                    [{ width: `${fromWidth}px` }, { width: `${toWidth}px` }],
                    { duration: spring.durationMs, easing: spring.easing }
                )
                current.onfinish = () => {
                    if (morph === current) settleMorph()
                }
                morph = current
            }
            islandWidth = toWidth

            for (const item of isResizing ? [] : items()) {
                const from = fromOffsets.get(item)
                if (from === undefined || isHidden(item)) continue
                const shift = from - centerOffsetOf(item)
                if (Math.abs(shift) < 0.5) continue
                glides.set(
                    item,
                    item.animate(
                        [
                            { transform: `translateX(${shift}px)` },
                            { transform: "translateX(0)" },
                        ],
                        { duration: spring.durationMs, easing: spring.easing }
                    )
                )
            }

            arrivalDirections(records, content).forEach((direction, el) => {
                el.animate(
                    direction === FADE_IN_DIRECTION
                        ? FADE_IN
                        : rollIn(direction),
                    {
                        duration: ISLAND_FADE.durationMs,
                        easing: ISLAND_FADE.easing,
                    }
                )
            })
            rememberOffsets()
        }

        const observer = new MutationObserver(records => {
            pending.push(...records)
            if (!frame) frame = requestAnimationFrame(animateChange)
        })
        observer.observe(content, WATCHED)
        observer.observe(island, ATTACHED_WATCHED)
        isEmptyBefore = items().every(isHidden)
        rememberOffsets()

        const onResize = () => {
            const isMoving =
                morph !== null ||
                [...glides.values()].some(
                    glide => glide.playState === "running"
                )
            if (isMoving) {
                if (!frame) frame = requestAnimationFrame(animateChange)
                return
            }
            islandWidth = island.offsetWidth
            rememberOffsets()
        }
        window.addEventListener("resize", onResize)

        return () => {
            observer.disconnect()
            cancelAnimationFrame(frame)
            window.removeEventListener("resize", onResize)
            stopMorph()
            stopGlides()
        }
    }, [islandRef, contentRef])
}
